import React, { useCallback, useState } from 'react';
import { Button, CenteredModal, useToast } from '@app/core';
import { Trans, useTranslation } from 'react-i18next';
import { Analytics } from '@app/analytics';
import {
  Participation,
  usePublicSession,
  useSendNotAvailableNotification,
} from '@app/api';
import { TextArea } from '@app/form';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useLoginParticipantWithModal } from '@app/auth';

interface Props {
  className?: string;
  participation: Participation;
  sessionId: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  description: {
    ...theme.fonts.label,
    fontWeight: 'normal',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const initialValues = {
  content: '',
};

export const NotAvailableButton = (props: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const { participation } = props;
  const participant = participation.participant;
  const { mutateAsync: sendNotification } = useSendNotAvailableNotification(
    props.sessionId,
  );
  const login = useLoginParticipantWithModal();
  const { data: session } = usePublicSession(participation.sessionId);
  const accountSchema = Yup.object().shape({
    content: Yup.string().required(),
  });

  const sendMessage = async (values: { content: string }) => {
    if (!participant) {
      return;
    }
    try {
      await sendNotification({
        participationId: participation.id,
        message: values.content,
      });
      setOpen(false);
      setOpenConfirmation(true);
      toast.saved();
    } catch (e) {
      Analytics.error(e);
      toast.exception(e);
    }
  };

  const openModal = useCallback(async () => {
    try {
      if (!participation.participantId) {
        return;
      }
      await login(participation.participantId);
      setOpen(true);
    } catch (e) {
      return;
    }
  }, [participation.participantId, login]);

  if (!session || !participation.participantId) {
    return null;
  }

  return (
    <React.Fragment>
      <Button
        type="button"
        color="ink"
        variant="muted"
        onClick={openModal}
        text={t('booking.appointment.notAvailable')}
        className={props.className}
      />
      <CenteredModal
        isOpen={open}
        onRequestClose={() => setOpen(false)}
        title={t('booking.appointment.notAvailableModal.title')}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={accountSchema}
          onSubmit={sendMessage}
        >
          <Form>
            <div className={classes.description}>
              <Trans
                i18nKey="booking.appointment.notAvailableModal.description"
                values={{
                  duration: session.duration,
                }}
                components={{
                  b: <b />,
                }}
              />
            </div>
            <TextArea
              placeholder={t(
                'booking.appointment.notAvailableModal.textArea.placeHolder',
              )}
              name="content"
              label={t('booking.appointment.notAvailableModal.textArea.label')}
            />
            <div className={classes.buttonContainer}>
              <Button color="green" small type="submit">
                <Trans i18nKey="booking.appointment.notAvailableModal.button" />
              </Button>
            </div>
          </Form>
        </Formik>
      </CenteredModal>

      <CenteredModal
        isOpen={openConfirmation}
        onRequestClose={() => setOpenConfirmation(false)}
        title={t('booking.appointment.notAvailableModal.confirmation.title')}
        text={t(
          'booking.appointment.notAvailableModal.confirmation.description',
        )}
      />
    </React.Fragment>
  );
};
